import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../services/api';
import type { ParsedItem } from '../types';

interface CreateChecklistParams {
  name: string;
  description?: string;
}

interface AddItemParams {
  checklistId: string;
  item: {
    productCode?: string;
    itemName: string;
    quantityNeeded: number;
  };
}

interface UpdateItemParams {
  checklistId: string;
  itemId: string;
  data: {
    quantityNeeded?: number;
    quantityPurchased?: number;
    actualPrice?: number;
    status?: string;
    notes?: string;
  };
}

interface DeleteItemParams {
  checklistId: string;
  itemId: string;
}

interface ToggleWatchlistParams {
  checklistId: string;
  itemId: string;
}

interface ImportParams {
  checklistId: string;
  items: ParsedItem[];
}

/**
 * Hook for all checklist mutations
 */
export function useChecklistMutations(
  selectedChecklistId: string | null,
  callbacks?: {
    onCreateSuccess?: (data: { id?: string }) => void;
    onDeleteSuccess?: () => void;
    onAddItemSuccess?: () => void;
    onUpdateItemSuccess?: () => void;
    onDeleteItemSuccess?: () => void;
    onImportSuccess?: (data: { imported?: number }) => void;
    onAddAllToWatchlistSuccess?: (data: { added?: number }) => void;
  }
) {
  const queryClient = useQueryClient();

  // Create checklist mutation
  const createMutation = useMutation({
    mutationFn: ({ name, description }: CreateChecklistParams) =>
      api.createChecklist(name, description),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      callbacks?.onCreateSuccess?.(data);
    },
  });

  // Delete checklist mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteChecklist(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      callbacks?.onDeleteSuccess?.();
    },
  });

  // Add item mutation
  const addItemMutation = useMutation({
    mutationFn: ({ checklistId, item }: AddItemParams) =>
      api.addChecklistItem(checklistId, {
        productCode: item.productCode || '',
        quantityNeeded: item.quantityNeeded,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist', selectedChecklistId] });
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      callbacks?.onAddItemSuccess?.();
    },
  });

  // Update item mutation
  const updateItemMutation = useMutation({
    mutationFn: ({ checklistId, itemId, data }: UpdateItemParams) =>
      api.updateChecklistItem(checklistId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist', selectedChecklistId] });
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      callbacks?.onUpdateItemSuccess?.();
    },
  });

  // Delete item mutation
  const deleteItemMutation = useMutation({
    mutationFn: ({ checklistId, itemId }: DeleteItemParams) =>
      api.deleteChecklistItem(checklistId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist', selectedChecklistId] });
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      callbacks?.onDeleteItemSuccess?.();
    },
  });

  // Toggle watchlist mutation
  const toggleWatchlistMutation = useMutation({
    mutationFn: ({ checklistId, itemId }: ToggleWatchlistParams) =>
      api.toggleChecklistItemWatchlist(checklistId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist', selectedChecklistId] });
    },
  });

  // Add all to watchlist mutation
  const addAllToWatchlistMutation = useMutation({
    mutationFn: (checklistId: string) => api.addAllChecklistToWatchlist(checklistId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['checklist', selectedChecklistId] });
      callbacks?.onAddAllToWatchlistSuccess?.(data);
    },
  });

  // Import items mutation
  const importMutation = useMutation({
    mutationFn: ({ checklistId, items }: ImportParams) =>
      api.importChecklistItems(checklistId, items.map(i => ({
        productCode: i.product_code,
        quantityNeeded: i.quantity_needed,
        estimatedPrice: i.estimated_price,
      }))),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['checklist', selectedChecklistId] });
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
      callbacks?.onImportSuccess?.(data);
    },
  });

  return {
    createMutation,
    deleteMutation,
    addItemMutation,
    updateItemMutation,
    deleteItemMutation,
    toggleWatchlistMutation,
    addAllToWatchlistMutation,
    importMutation,
  };
}
